/**
 * ARIA Admin Reset Password Modal
 *
 * Admin-only password reset (ARIA auth Option B — see AUTH_ENTRA_ID_SSO_PLAN.md).
 * There is no self-service reset yet — this is the only way to recover a
 * forgotten password until email-based reset is built.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, gql } from '@apollo/client';
import { X } from 'lucide-react';

const ADMIN_RESET_PASSWORD_MUTATION = gql`
  mutation AdminResetPassword($id: ID!, $newPassword: String!) {
    adminResetPassword(id: $id, newPassword: $newPassword)
  }
`;

interface ResetPasswordModalProps {
  user: { id: string; email: string; name: string } | null;
  onClose: () => void;
  onReset: () => void;
}

export function ResetPasswordModal({ user, onClose, onReset }: ResetPasswordModalProps) {
  const [newPassword, setNewPassword] = useState('');
  const [resetPassword, { loading, error }] = useMutation(ADMIN_RESET_PASSWORD_MUTATION);

  if (!user) return null;

  const handleClose = () => {
    setNewPassword('');
    onClose();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await resetPassword({ variables: { id: user.id, newPassword } });
      setNewPassword('');
      onReset();
      onClose();
    } catch {
      // surfaced via `error` below
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black/50 transition-opacity" onClick={handleClose} />

      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Reset Password</h2>
              <p className="text-sm text-gray-500">{user.name} — {user.email}</p>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New password</label>
              <input
                type="text"
                required
                minLength={8}
                autoComplete="new-password"
                className="input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">At least 8 characters. Share this with the user directly.</p>
            </div>

            {error && <p className="text-sm text-red-600">{error.message}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleClose} className="btn-secondary">
                Cancel
              </button>
              <button type="submit" disabled={loading} className="btn-primary">
                {loading ? 'Resetting…' : 'Reset password'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
